/**
 * Cadastro de produto — editor (`/produtos/novo` e `/produtos/:productId`).
 *
 * `:productId` é o UUID `products.id`, NUNCA o SKU: há SKUs com `/` e `%`,
 * que o React Router decodifica antes de casar a rota.
 *
 * Página própria, dentro do Layout normal (rola a JANELA): cabeçalho fixo com
 * Descartar/Salvar, identidade com preço/custo/margem do rascunho, lista de
 * pendências calculadas pelo servidor, navegação lateral fixa com scroll-spy e
 * as seções. O rascunho só grava no Salvar (PUT só com os campos alterados);
 * Volumoso e Rastreia lote vão pelo endpoint do WMS (`wms:manage:products`).
 *
 * Sem write-back: nada daqui vai para o Bling. O `bling_snapshot` só aparece
 * como dica ("Usar valor do Bling" preenche o campo; grava com o Salvar).
 *
 * Limitação aceita: o App usa `BrowserRouter` (sem `useBlocker`), então um
 * clique na sidebar não pede confirmação — o cabeçalho mostra "Alterações não
 * salvas", o "← Produtos" confirma e o `beforeunload` cobre fechar/recarregar.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'shared/icons';
import ConfirmModal from '../../components/ui/ConfirmModal';
import ErrorCard from '../../components/ui/ErrorCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../hooks/useAuth';
import { EditorHeader, NewBrandModal, PendingList, ProductIdentity, SectionNav, SectionNotice } from './editorParts';
import { FieldDomId, PendingSections, SectionOfField, UpperCollapse, VisibleSections } from './productForm';
import BarcodesSection from './sections/BarcodesSection';
import GeneralSection from './sections/GeneralSection';
import KitNoticeSection from './sections/KitNoticeSection';
import LogisticsSection from './sections/LogisticsSection';
import StockSection from './sections/StockSection';
import SupplierSection from './sections/SupplierSection';
import TaxSection from './sections/TaxSection';
import { useProductEditor } from './useProductEditor';
import { useSectionSpy } from './useSectionSpy';

// Altura do cabeçalho fixo + folga: onde o scroll-spy considera "o topo".
const SPY_OFFSET_PX = 96;
// Espera a rolagem suave até a seção antes de focar o campo com erro.
const FOCUS_AFTER_SCROLL_MS = 350;

function focusField(field) {
    const element = document.getElementById(FieldDomId(field));
    if (element && typeof element.focus === 'function') {
        element.focus({ preventScroll: true });
    }
}

const ProductEditorPage = ({ mode = 'edit', productId = null }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canEdit = checkPermission('stock:write');
    const canManageWms = checkPermission('wms:manage:products');
    const canCreateBrand = checkPermission('organizations:write');
    const isNew = mode === 'new';
    const readOnly = !canEdit;

    const editor = useProductEditor({ mode, productId, canEdit, canManageWms });
    const { form, product, errors, warnings, dirtyFields, isDirty, saving } = editor;

    const sections = useMemo(() => VisibleSections(form.format), [form.format]);
    const sectionIds = useMemo(() => sections.map((section) => section.id), [sections]);
    const { activeId, scrollToSection } = useSectionSpy(sectionIds, { offsetPx: SPY_OFFSET_PX });

    const [confirm, setConfirm] = useState(null);
    const [brandModalOpen, setBrandModalOpen] = useState(false);

    const backTo =
        typeof location.state?.from === 'string' && location.state.from.startsWith('/produtos')
            ? location.state.from
            : '/produtos';

    // ── Navegação lateral: erro e pendência por seção ────────────────────────

    const pendingSections = useMemo(() => PendingSections(product?.pending), [product]);
    const navSections = useMemo(
        () =>
            sections.map((section) => ({
                id: section.id,
                label: section.label,
                errorCount: section.fields.filter((field) => errors[field]).length,
                hasPending: pendingSections.has(section.id) || section.fields.some((field) => warnings[field]),
            })),
        [sections, errors, warnings, pendingSections],
    );
    const sectionLabels = useMemo(
        () => Object.fromEntries(sections.map((section) => [section.id, section.label])),
        [sections],
    );

    // ── Salvar / descartar / sair ────────────────────────────────────────────

    const goToField = useCallback(
        (field) => {
            const sectionId = SectionOfField(field);
            if (sectionId) {
                scrollToSection(sectionId);
            }
            window.setTimeout(() => focusField(field), FOCUS_AFTER_SCROLL_MS);
        },
        [scrollToSection],
    );

    const { save } = editor;
    const handleSave = useCallback(async () => {
        const result = await save();
        if (!result.ok) {
            if (result.firstErrorField) {
                const count = result.errorCount || 1;
                toast.error(
                    result.serverError && result.message
                        ? result.message
                        : `Corrija ${count} ${count === 1 ? 'campo' : 'campos'} antes de salvar`,
                );
                goToField(result.firstErrorField);
            } else if (result.serverError) {
                toast.error(result.message || 'Não foi possível salvar');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            return;
        }
        if (result.noop) {
            return;
        }
        if (isNew) {
            toast.success(
                'Produto cadastrado. Para ter saldo vendável, cadastre o mesmo SKU no Bling; o vínculo é automático.',
            );
            if (result.wmsError) {
                toast.error(`Atributos do armazém não gravados: ${result.wmsError}`);
            }
            if (result.created?.id) {
                navigate(`/produtos/${result.created.id}`, { replace: true, state: location.state });
            }
            return;
        }
        if (result.wmsError) {
            toast.error(
                result.cadastroSaved
                    ? `Cadastro salvo; atributos do armazém não: ${result.wmsError}`
                    : `Atributos do armazém não gravados: ${result.wmsError}`,
            );
            return;
        }
        toast.success('Produto salvo');
    }, [save, toast, goToField, isNew, navigate, location.state]);

    const leave = useCallback(() => navigate(backTo), [navigate, backTo]);

    const requestBack = () => {
        if (isDirty && canEdit) {
            setConfirm('leave');
        } else {
            leave();
        }
    };

    const requestDiscard = () => {
        if (isNew) {
            if (isDirty) {
                setConfirm('discard-new');
            } else {
                leave();
            }
            return;
        }
        if (isDirty) {
            setConfirm('discard');
        }
    };

    const confirmAction = () => {
        const action = confirm;
        setConfirm(null);
        if (action === 'discard') {
            editor.discard();
        } else {
            leave();
        }
    };

    // Fechar/recarregar a aba com alteração pendente pede confirmação do navegador.
    useEffect(() => {
        if (!isDirty || !canEdit) {
            return undefined;
        }
        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty, canEdit]);

    // Ctrl/Cmd+S salva (fora de modal aberto).
    useEffect(() => {
        if (!canEdit) {
            return undefined;
        }
        const onKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 's') {
                event.preventDefault();
                if (!confirm && !brandModalOpen) {
                    handleSave();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [canEdit, confirm, brandModalOpen, handleSave]);

    // `/produtos/<id>#tributacao`: rola até a seção depois de carregar.
    const { status } = editor;
    const hash = location.hash;
    useEffect(() => {
        if (status !== 'ready' || !hash) {
            return undefined;
        }
        const target = hash.slice(1);
        if (!sectionIds.includes(target)) {
            return undefined;
        }
        const timer = window.setTimeout(() => scrollToSection(target), 50);
        return () => window.clearTimeout(timer);
        // Só na carga (e ao trocar de âncora), não a cada mudança de seções.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, hash]);

    // ── Estados de carga ─────────────────────────────────────────────────────

    const backButton = (
        <button
            type="button"
            onClick={leave}
            className="inline-flex items-center gap-1 text-sm text-mono-500 transition-colors hover:text-black dark:text-mono-400 dark:hover:text-white"
        >
            <ArrowLeft size={16} /> Produtos
        </button>
    );

    if (status === 'loading') {
        return (
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
                <div>{backButton}</div>
                <LoadingSpinner className="py-20" />
            </div>
        );
    }

    if (status === 'not_found') {
        return (
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
                <div>{backButton}</div>
                <div className="rounded-xl border border-mono-200 bg-white px-5 py-8 text-center dark:border-mono-800 dark:bg-mono-950">
                    <p className="font-mono text-base font-bold text-black dark:text-white">{productId}</p>
                    <p className="mt-1 text-sm text-mono-500 dark:text-mono-400">
                        Produto não encontrado neste escopo.
                    </p>
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
                <div>{backButton}</div>
                <ErrorCard
                    title="Produto"
                    message={`Não foi possível carregar o produto.\n${editor.loadError || ''}`}
                    action="Tentar novamente"
                    actionFn={editor.reload}
                />
            </div>
        );
    }

    // ── Página ───────────────────────────────────────────────────────────────

    const ctx = {
        mode,
        form,
        dirtyFields,
        errors,
        warnings,
        hints: editor.hints,
        setField: editor.setField,
        readOnly: readOnly || saving,
        suggestions: editor.blingSuggestions,
        capturedAt: product?.bling_snapshot?.captured_at ?? null,
    };
    const headerTitle = UpperCollapse(form.title) || (isNew ? 'Novo produto' : product?.sku || 'Produto');

    return (
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 pb-16">
            <EditorHeader
                mode={mode}
                title={headerTitle}
                onBack={requestBack}
                updatedAt={product?.updated_at}
                status={form.status}
                onToggleStatus={(value) => editor.setField('status', value)}
                canEdit={canEdit}
                isDirty={isDirty}
                dirtyCount={dirtyFields.size}
                saving={saving}
                onDiscard={requestDiscard}
                onSave={handleSave}
            />

            {readOnly ? (
                <SectionNotice tone="info">
                    Somente leitura: editar o cadastro exige a permissão de escrita de estoque.
                </SectionNotice>
            ) : null}

            {editor.formError ? (
                <div
                    role="alert"
                    className="rounded-lg border border-danger-border bg-danger-soft px-4 py-3 text-sm text-danger-text dark:border-danger-900/60 dark:bg-danger-900/20 dark:text-danger-300"
                >
                    {editor.formError}
                </div>
            ) : null}

            <ProductIdentity mode={mode} product={product} form={form} />

            {!isNew ? (
                <PendingList pending={product?.pending} sectionLabels={sectionLabels} onGo={scrollToSection} />
            ) : null}

            <div className="grid gap-8 lg:grid-cols-[11rem_minmax(0,1fr)]">
                <SectionNav sections={navSections} activeId={activeId} onSelect={scrollToSection} />
                <div className="flex min-w-0 flex-col gap-5">
                    <GeneralSection
                        ctx={ctx}
                        mode={mode}
                        product={product}
                        brands={editor.brands}
                        brandsLoading={editor.brandsLoading}
                        brandsError={editor.brandsError}
                        canCreateBrand={canCreateBrand}
                        onNewBrand={() => setBrandModalOpen(true)}
                        skuChecking={editor.skuChecking}
                    />
                    <BarcodesSection ctx={ctx} />
                    <LogisticsSection ctx={ctx} />
                    {form.format === 'KIT' ? <KitNoticeSection /> : null}
                    <StockSection ctx={ctx} mode={mode} product={product} canManageWms={canManageWms} />
                    <SupplierSection ctx={ctx} />
                    <TaxSection ctx={ctx} />
                </div>
            </div>

            <ConfirmModal
                isOpen={confirm === 'discard'}
                onClose={() => setConfirm(null)}
                onConfirm={confirmAction}
                title="Descartar alterações?"
                message={`${dirtyFields.size} ${dirtyFields.size === 1 ? 'campo volta' : 'campos voltam'} ao valor salvo.`}
                confirmText="Descartar"
                confirmVariant="danger"
            />
            <ConfirmModal
                isOpen={confirm === 'leave' || confirm === 'discard-new'}
                onClose={() => setConfirm(null)}
                onConfirm={confirmAction}
                title="Sair sem salvar?"
                message={
                    isNew
                        ? 'O produto ainda não foi cadastrado; o que você preencheu se perde.'
                        : `${dirtyFields.size} ${dirtyFields.size === 1 ? 'alteração não salva se perde' : 'alterações não salvas se perdem'}.`
                }
                confirmText="Sair sem salvar"
                confirmVariant="danger"
            />
            {canCreateBrand ? (
                <NewBrandModal
                    isOpen={brandModalOpen}
                    onClose={() => setBrandModalOpen(false)}
                    brands={editor.brands}
                    onCreated={async (brand) => {
                        await editor.reloadBrands();
                        editor.setField('brand', brand);
                        toast.success(`Marca ${brand} disponível`);
                    }}
                />
            ) : null}
        </div>
    );
};

/**
 * Rota `/produtos/:productId`: lê o id e remonta o editor ao trocar de produto
 * (`key`), para nenhum estado do anterior vazar.
 */
export const ProductEditorRoute = () => {
    const { productId } = useParams();
    return <ProductEditorPage key={productId} mode="edit" productId={productId} />;
};

export default ProductEditorPage;
